/**
 * Browser Fingerprinting Utility
 * 
 * Generates a consistent browser signature to help identify browser-based requests
 * and distinguish them from API clients like Postman or curl.
 */

export interface BrowserFingerprint {
  userAgent: string;
  language: string;
  languages: string[];
  platform: string;
  screenResolution: string;
  colorDepth: number;
  timezone: string;
  timezoneOffset: number;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  hardwareConcurrency: number;
  deviceMemory: number | null;
  touchSupport: boolean;
  webglVendor: string | null;
  webglRenderer: string | null;
}

export interface BrowserSignature {
  fingerprint: BrowserFingerprint;
  signature: string;
  clientType: 'web-browser';
  generatedAt: number;
}

/**
 * Simple hash function for generating fingerprint signature
 * Uses a basic string hashing algorithm for consistency
 */
const hashString = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString(36);
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert to base36 and ensure positive
  return Math.abs(hash).toString(36);
};

/**
 * Get WebGL renderer info for fingerprinting
 */
const getWebGLInfo = (): { vendor: string | null; renderer: string | null } => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return {
          vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
          renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
        };
      }
    }
  } catch (e) {
    // WebGL not available
  }
  
  return { vendor: null, renderer: null };
};

/**
 * Check if touch is supported
 */
const isTouchSupported = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - for older browsers
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Collect browser fingerprint data
 */
export const collectFingerprint = (): BrowserFingerprint => {
  const webglInfo = getWebGLInfo();
  
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: Array.from(navigator.languages || [navigator.language]),
    platform: navigator.platform,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || null,
    hardwareConcurrency: navigator.hardwareConcurrency || 1,
    // @ts-ignore - deviceMemory is not in all browsers
    deviceMemory: navigator.deviceMemory || null,
    touchSupport: isTouchSupported(),
    webglVendor: webglInfo.vendor,
    webglRenderer: webglInfo.renderer,
  };
};

/**
 * Generate a signature hash from the fingerprint
 * This creates a compact string that represents the browser environment
 */
export const generateSignature = (fingerprint: BrowserFingerprint): string => {
  // Combine key fingerprint elements into a string
  const components = [
    fingerprint.userAgent,
    fingerprint.language,
    fingerprint.platform,
    fingerprint.screenResolution,
    fingerprint.colorDepth.toString(),
    fingerprint.timezone,
    fingerprint.hardwareConcurrency.toString(),
    fingerprint.touchSupport.toString(),
    fingerprint.webglVendor || 'unknown',
    fingerprint.webglRenderer || 'unknown',
  ];
  
  const combinedString = components.join('|');
  
  // Generate multiple hashes for more uniqueness
  const hash1 = hashString(combinedString);
  const hash2 = hashString(combinedString.split('').reverse().join(''));
  const hash3 = hashString(fingerprint.userAgent + fingerprint.timezone);
  
  return `${hash1}-${hash2}-${hash3}`;
};

/**
 * Get complete browser signature with fingerprint and hash
 */
export const getBrowserSignature = (): BrowserSignature => {
  const fingerprint = collectFingerprint();
  const signature = generateSignature(fingerprint);
  
  return {
    fingerprint,
    signature,
    clientType: 'web-browser',
    generatedAt: Date.now(),
  };
};

/**
 * Get just the signature string (for headers)
 */
export const getSignatureString = (): string => {
  const fingerprint = collectFingerprint();
  return generateSignature(fingerprint);
};

/**
 * Get device ID from localStorage, or generate a new one
 */
export const getDeviceId = (): string | null => {
  return localStorage.getItem('deviceId');
};

/**
 * Store device ID in localStorage
 */
export const setDeviceId = (deviceId: string): void => {
  localStorage.setItem('deviceId', deviceId);
};

/**
 * Clear device ID from localStorage
 */
export const clearDeviceId = (): void => {
  localStorage.removeItem('deviceId');
};

/**
 * Generate a new device ID (for initial login when backend doesn't provide one)
 * Format: browser-signature-timestamp-random
 */
export const generateDeviceId = (): string => {
  const signature = getSignatureString();
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `${signature.substring(0, 8)}-${timestamp}-${random}`;
};

/**
 * Get headers object to include in API requests
 */
export const getBrowserSecurityHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'X-Browser-Signature': getSignatureString(),
    'X-Client-Type': 'web-browser',
  };
  
  const deviceId = getDeviceId();
  if (deviceId) {
    headers['X-Device-ID'] = deviceId;
  }
  
  return headers;
};

export default {
  collectFingerprint,
  generateSignature,
  getBrowserSignature,
  getSignatureString,
  getDeviceId,
  setDeviceId,
  clearDeviceId,
  generateDeviceId,
  getBrowserSecurityHeaders,
};

